import { useEffect, useId, useRef, type ReactNode } from "react";
import { IconButton } from "./Button";

const focusableSelector =
  "input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex='-1'])";

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  closeLabel?: string;
  className?: string;
  closeDisabled?: boolean;
  initialFocusSelector?: string;
};

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  closeLabel = "Close",
  className = "",
  closeDisabled = false,
  initialFocusSelector
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const frameRef = useRef<number | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;

    const restoreFocus = () => {
      const target = returnFocusRef.current;
      returnFocusRef.current = null;
      queueMicrotask(() => {
        if (target?.isConnected) target.focus();
      });
    };

    if (open) {
      if (!dialog.open) {
        returnFocusRef.current = document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
        dialog.showModal();
      }
      frameRef.current = requestAnimationFrame(() => {
        if (!dialog.open) return;
        const requested = initialFocusSelector
          ? dialog.querySelector<HTMLElement>(initialFocusSelector)
          : null;
        const fallback = dialog.querySelector<HTMLElement>(focusableSelector);
        (requested ?? fallback ?? dialog).focus();
      });
    } else {
      if (dialog.open) dialog.close();
      restoreFocus();
    }

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      if (dialog.open) dialog.close();
      restoreFocus();
    };
  }, [open, initialFocusSelector]);

  return (
    <dialog
      ref={ref}
      className={`nx-dialog ${className}`.trim()}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      aria-busy={closeDisabled || undefined}
      tabIndex={-1}
      onCancel={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!closeDisabled) onClose();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Tab") return;
        const dialog = event.currentTarget;
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest("dialog") !== dialog) return;

        event.stopPropagation();
        const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector))
          .filter((element) => element.closest("dialog") === dialog && element.getClientRects().length > 0);

        if (focusable.length === 0) {
          event.preventDefault();
          dialog.focus();
          return;
        }

        const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const activeIndex = active ? focusable.indexOf(active) : -1;

        if (event.shiftKey) {
          if (activeIndex <= 0) {
            event.preventDefault();
            focusable.at(-1)?.focus();
          }
          return;
        }

        if (activeIndex === -1 || activeIndex === focusable.length - 1) {
          event.preventDefault();
          focusable[0]?.focus();
        }
      }}
    >
      <header className="nx-dialog__header">
        <div>
          <h2 id={titleId}>{title}</h2>
          {description ? <p id={descriptionId} className="nx-card__description">{description}</p> : null}
        </div>
        <IconButton
          icon="close"
          label={closeLabel}
          variant="ghost"
          size="sm"
          disabled={closeDisabled}
          onClick={onClose}
        />
      </header>
      <div className="nx-dialog__body">{children}</div>
      {footer ? <footer className="nx-dialog__footer">{footer}</footer> : null}
    </dialog>
  );
}
